import { IconButton, Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import DashboardInfoDialog from "./DashboardInfo/DashboardInfoDialog";
import { useState, useRef, useEffect } from "react";

const SIDEBAR_WIDTH = 260;   
const HEADER_HEIGHT = 80;    
const BUTTON_SIZE = 48;
const PADDING = 10;          

const STORAGE_KEY = "infoButtonPosition";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getBounds = () => ({
  minX: SIDEBAR_WIDTH + PADDING,
  minY: HEADER_HEIGHT + PADDING,
  maxX: window.innerWidth - BUTTON_SIZE - PADDING,
  maxY: window.innerHeight - BUTTON_SIZE - PADDING,
});

const getDefaultPosition = () => ({
  x: window.innerWidth - BUTTON_SIZE - PADDING - 16,
  y: window.innerHeight - BUTTON_SIZE - PADDING - 16,
});

const loadSavedPosition = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (!saved) return null;
  const parsed = JSON.parse(saved);
  const bounds = getBounds();
  return {
    x: clamp(parsed.x, bounds.minX, bounds.maxX),
    y: clamp(parsed.y, bounds.minY, bounds.maxY),
  };
};

const savePosition = (pos) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
};

export default function InfoButton({ pageName }) {
  const [open, setOpen] = useState(false);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const hasDragged = useRef(false);
  const buttonRef = useRef(null);

  const [position, setPosition] = useState(() => loadSavedPosition() ?? { x: 0, y: 0 });

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      setPosition(getDefaultPosition());
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => {
        const bounds = getBounds();
        return {
          x: clamp(prev.x, bounds.minX, bounds.maxX),
          y: clamp(prev.y, bounds.minY, bounds.maxY),
        };
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const updatePosition = (x, y) => {
    const bounds = getBounds();
    const clamped = {
      x: clamp(x, bounds.minX, bounds.maxX),
      y: clamp(y, bounds.minY, bounds.maxY),
    };
    setPosition(clamped);
    savePosition(clamped);
  };

  const handleMouseDown = (e) => {
    isDragging.current = true;
    hasDragged.current = false;
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    e.preventDefault();
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    isDragging.current = true;
    hasDragged.current = false;
    dragStart.current = {
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    };
  };

  useEffect(() => {
       const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      hasDragged.current = true;
      updatePosition(
        e.clientX - dragStart.current.x,
        e.clientY - dragStart.current.y
      );
    };

    const handleTouchMove = (e) => {
      if (!isDragging.current) return;
      hasDragged.current = true;
      const touch = e.touches[0];
      updatePosition(
        touch.clientX - dragStart.current.x,
        touch.clientY - dragStart.current.y
      );
    };

    const handleMouseUp = () => { isDragging.current = false; };
    const handleTouchEnd = () => { isDragging.current = false; };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [position]);

  const handleClick = () => {
    if (!hasDragged.current) setOpen(true);
  };


  return (
    <>
    <Tooltip title="Page Info" placement="left">
      <IconButton
        ref={buttonRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onClick={handleClick}
        aria-label="info"
        sx={{
            position: "fixed",
            left: position.x,
            top: position.y,
            zIndex: 1300,
            cursor: "grab",
            backgroundColor: "#673ab7",
            color: "#fff",
            width: BUTTON_SIZE,
            height: BUTTON_SIZE,
            boxShadow: "0px 4px 12px rgba(0,0,0,0.3)",
            "&:active": { cursor: "grabbing" },
            "&:hover": {
              backgroundColor: "#512da8",
              boxShadow: "0px 6px 16px rgba(0,0,0,0.4)",
            },
          }}
      >
        <InfoIcon />
      </IconButton>
    </Tooltip>
    <DashboardInfoDialog
      open={open}
      onClose={() => setOpen(false)}
      pageName={pageName}
      />
    </>
  );
}